import { useTranslation } from 'react-i18next';
import { Icon } from './Icons';

// Step state — three flavours: a completed step renders a check + tinted fill, the active
// step has a solid ink fill with its number in white, and a future step is a muted outline.
export type StepState = 'completed' | 'active' | 'future';

export interface StepperStep {
  // 1-indexed step number; shown for active + future, replaced by a check on completed.
  number: number;
  label: string;
  state: StepState;
}

interface StepperProps {
  steps: StepperStep[];
  // When set, bullets become clickable buttons calling back with the step number. Used by
  // the wizard-mode editor to navigate between sections; when omitted the stepper is a
  // read-only progress indicator (the original mode).
  onStepClick?: (number: number) => void;
  className?: string;
}

// Horizontal progress stepper for multi-step authoring flows. State of each step is
// computed by the parent (e.g. from the clip's status + tagging fields). Renders a
// connector line between adjacent steps so completed runs read at a glance.
export function Stepper({ steps, onStepClick, className = '' }: StepperProps) {
  return (
    <div className={'rounded border border-line bg-surface p-4 shadow-1 ' + className}>
      <ol className="flex items-center gap-3">
        {steps.map((step, idx) => (
          <li key={step.number} className="flex flex-1 items-center gap-3 last:flex-none">
            <StepBullet step={step} onClick={onStepClick ? () => onStepClick(step.number) : undefined} />
            <div className="min-w-0 flex-1">
              <div
                className={
                  'truncate text-[13px] font-semibold ' +
                  (step.state === 'future' ? 'text-muted' : 'text-ink')
                }
              >
                {step.label}
              </div>
            </div>
            {/* Connector line — drawn between two adjacent steps. The last step has no line
                (and the parent `last:flex-none` collapses its trailing slot). */}
            {idx < steps.length - 1 && (
              <div
                className={
                  'h-px flex-1 ' +
                  (step.state === 'completed' ? 'bg-ink' : 'bg-line-2')
                }
                aria-hidden="true"
              />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function StepBullet({ step, onClick }: { step: StepperStep; onClick?: () => void }) {
  const { t } = useTranslation();
  // Readable equivalent of the visual state, for the non-interactive bullet's accessible name.
  const stateLabel = t(`components.stepper.state.${step.state}`);
  const base = 'grid h-8 w-8 flex-none place-items-center rounded-sm font-mono text-[13px] font-bold';
  const tone =
    step.state === 'completed'
      ? 'bg-ink text-bg'
      : step.state === 'active'
        ? 'bg-accent text-accent-ink'
        : 'border border-line-2 bg-surface text-muted';
  const interactive = onClick ? ' cursor-pointer transition-transform hover:scale-105' : '';
  const content =
    step.state === 'completed' ? <Icon.check className="w-4 h-4" /> : step.number;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={base + ' ' + tone + interactive}
        aria-label={t('components.stepper.goToStepAria', { number: step.number, label: step.label })}
      >
        {content}
      </button>
    );
  }
  // A non-interactive bullet is a bare number (or a check glyph) with no accessible name — the
  // state it encodes is carried purely by colour, which a screen reader can't see. Spell it out.
  return (
    <div
      className={base + ' ' + tone}
      aria-label={t('components.stepper.stepAria', { number: step.number, label: step.label, state: stateLabel })}
    >
      {content}
    </div>
  );
}
